import React from "react";
import PropTypes from "prop-types";
import { useSearchParams } from "react-router-dom";
import { Box, CircularProgress } from "@material-ui/core";
import { useVerificarTokenQRQuery } from "src/services/qrVerificationApi";
import { BRAND } from "src/theme/brandTokens";
import DetallesV1 from "./DetallesV1";
import DetallesV2 from "./DetallesV2";

// ─── parseRtkError ──────────────────────────────────────────────────────────
//
// Devuelve { title, message, severity } o null. Función pura que mapea
// errores de RTK Query a textos legibles para el usuario.
export const parseRtkError = (err) => {
  if (!err) return null;

  if (typeof err.status === "string") {
    const code = err.status;
    if (code === "FETCH_ERROR" || code === "TIMEOUT_ERROR") {
      return {
        title: "Error de conexión",
        message:
          "No se pudo contactar al servidor. Verifica tu internet e intenta nuevamente.",
        severity: "error",
      };
    }
    if (code === "PARSING_ERROR") {
      return {
        title: "Respuesta inválida",
        message: "El servidor devolvió un formato inesperado.",
        severity: "error",
      };
    }
    return { title: "Error", message: String(code), severity: "error" };
  }

  const status = Number(err.status);
  const body = err.data ?? {};
  const backendMsg =
    process.env.NODE_ENV === "development"
      ? (body?.message ??
        body?.error ??
        (typeof body === "string" ? body : undefined))
      : null;

  if (status === 400) {
    return {
      title: "Solicitud inválida",
      message: backendMsg ?? "Los datos enviados no son válidos.",
      severity: "warning",
    };
  }
  if (status === 401) {
    return {
      title: "No autenticado",
      message: backendMsg ?? "Debes iniciar sesión para continuar.",
      severity: "warning",
    };
  }
  if (status === 403) {
    return {
      title: "Sin permisos",
      message: backendMsg ?? "No tienes permisos para realizar esta acción.",
      severity: "warning",
    };
  }
  if (status === 404) {
    return {
      title: "Código QR no encontrado",
      message: backendMsg ?? "El token no existe o ya expiró.",
      severity: "warning",
    };
  }
  if (status === 409) {
    return {
      title: "Conflicto",
      message:
        backendMsg ?? "La operación entra en conflicto con el estado actual.",
      severity: "warning",
    };
  }
  if (status === 422) {
    return {
      title: "Datos no procesables",
      message: backendMsg ?? "Hay errores de validación en los datos enviados.",
      severity: "warning",
    };
  }
  if ([500, 502, 503, 504].includes(status)) {
    return {
      title: "Servicio no disponible",
      message:
        backendMsg ?? "Ocurrió un error en el servidor. Intenta más tarde.",
      severity: "error",
    };
  }

  return {
    title: `Error ${status}`,
    message: backendMsg ?? "Ocurrió un error inesperado.",
    severity: "error",
  };
};

// ─── Sub-componentes de layout ──────────────────────────────────────────────

const PageContainer = ({ children }) => (
  <Box
    sx={{
      minHeight: "100vh",
      width: "100%",
      bgcolor: BRAND.color.grey10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      p: { xs: 3, md: 4 },
      fontFamily: BRAND.font.family.base,
    }}
  >
    {children}
  </Box>
);

const Card = ({ children }) => (
  <Box
    sx={{
      position: "relative",
      width: "100%",
      maxWidth: 960,
      bgcolor: BRAND.color.paper,
      p: { xs: 4, md: 6 },
      pt: { xs: 5, md: 6 },
      overflow: "hidden",
    }}
  >
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        top: 0,
        right: 0,
        width: 64,
        height: 64,
        bgcolor: BRAND.color.primary,
      }}
    />
    {children}
  </Box>
);

// Encabezado eyebrow + título mega del card. Usado tanto por estados de éxito
// como por estados de error/sin-token, con eyebrow contextual.
const Encabezado = ({ eyebrow, titulo }) => (
  <Box sx={{ pr: { xs: "70px", md: "80px" }, mb: { xs: 3, md: 4 } }}>
    <Box
      sx={{
        fontSize: BRAND.font.size.eyebrow,
        color: BRAND.color.primary,
        textTransform: "uppercase",
        letterSpacing: BRAND.font.letterSpacing.widest,
        fontWeight: BRAND.font.weight.medium,
        mb: 1.5,
      }}
    >
      {eyebrow}
    </Box>
    <Box
      component="h1"
      sx={{
        m: 0,
        fontFamily: BRAND.font.family.display,
        fontSize: { xs: "26pt", md: BRAND.font.size.h2 },
        fontWeight: BRAND.font.weight.black,
        color: BRAND.color.inkSoft,
        lineHeight: BRAND.font.lineHeight.tight,
        letterSpacing: BRAND.font.letterSpacing.tight,
      }}
    >
      {titulo}
    </Box>
  </Box>
);

const MensajeBody = ({ children }) => (
  <Box
    sx={{
      fontSize: BRAND.font.size.bodyLarge,
      color: BRAND.color.grey70,
      lineHeight: BRAND.font.lineHeight.body,
      maxWidth: 640,
    }}
  >
    {children}
  </Box>
);

// ─── Componente principal ───────────────────────────────────────────────────

const VerificarQR = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  const {
    data: resultado,
    error,
    isLoading,
    isSuccess,
    isError,
  } = useVerificarTokenQRQuery(token, { skip: !token });

  // Loading
  if (token && isLoading) {
    return (
      <PageContainer>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 3,
          }}
        >
          <CircularProgress
            size={60}
            thickness={4}
            sx={{ color: BRAND.color.primary }}
          />
          <Box
            sx={{
              fontSize: BRAND.font.size.eyebrow,
              color: BRAND.color.grey50,
              textTransform: "uppercase",
              letterSpacing: BRAND.font.letterSpacing.widest,
              fontWeight: BRAND.font.weight.medium,
            }}
          >
            Verificando autenticidad…
          </Box>
        </Box>
      </PageContainer>
    );
  }

  // Sin token en la URL
  if (!token) {
    return (
      <PageContainer>
        <Card>
          <Encabezado
            eyebrow="Token ausente"
            titulo="Esta página requiere un código válido"
          />
          <MensajeBody>
            Asegúrate de acceder mediante el código QR de una propuesta firmada.
            El enlace debe contener un token de verificación.
          </MensajeBody>
        </Card>
      </PageContainer>
    );
  }

  // Error de la query (red, HTTP, etc.)
  if (isError) {
    const ui = parseRtkError(error);
    return (
      <PageContainer>
        <Card>
          <Encabezado
            eyebrow={ui?.title || "Error"}
            titulo="No pudimos completar la verificación"
          />
          <MensajeBody>
            {ui?.message ||
              "Ocurrió un error inesperado al validar el código. Intenta nuevamente más tarde."}
          </MensajeBody>
        </Card>
      </PageContainer>
    );
  }

  // QR inválido (respuesta exitosa pero esValido=false)
  if (isSuccess && !resultado?.esValido) {
    return (
      <PageContainer>
        <Card>
          <Encabezado
            eyebrow="QR inválido"
            titulo="No pudimos validar este código"
          />
          <MensajeBody>
            El código que estás escaneando no corresponde a una propuesta
            firmada en nuestro sistema, o el token ha sido invalidado.
            {resultado?.codigo && (
              <Box
                component="span"
                sx={{
                  display: "block",
                  mt: 2,
                  fontSize: BRAND.font.size.bodySmall,
                  color: BRAND.color.grey50,
                }}
              >
                Código de error: {resultado.codigo}
              </Box>
            )}
          </MensajeBody>
        </Card>
      </PageContainer>
    );
  }

  // QR válido — render con detalles
  if (isSuccess && resultado?.esValido) {
    const propuestaId = resultado.detalles?.propuestaId ?? "—";
    const esV2 = resultado.version === "v2";

    return (
      <PageContainer>
        <Card>
          <Encabezado
            eyebrow="Verificación de autenticidad"
            titulo={`Propuesta N° ${propuestaId}`}
          />

          <Box
            component="hr"
            sx={{
              border: 0,
              borderTop: `1px solid ${BRAND.color.divider}`,
              m: 0,
              mb: { xs: 3, md: 4 },
            }}
          />

          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              gap: { xs: 2, md: 4 },
              alignItems: { xs: "flex-start", md: "baseline" },
              mb: { xs: 4, md: 5 },
            }}
          >
            <Box>
              <Box
                sx={{
                  fontSize: BRAND.font.size.eyebrow,
                  color: BRAND.color.primary,
                  textTransform: "uppercase",
                  letterSpacing: BRAND.font.letterSpacing.widest,
                  fontWeight: BRAND.font.weight.medium,
                  mb: 1,
                }}
              >
                Propuesta auténtica
              </Box>
              <Box
                sx={{
                  fontSize: BRAND.font.size.body,
                  color: BRAND.color.inkSoft,
                  lineHeight: BRAND.font.lineHeight.body,
                  maxWidth: 640,
                }}
              >
                Esta propuesta fue firmada formalmente por el cliente. La
                información que aparece a continuación corresponde al snapshot
                inmutable registrado al momento de la firma.
              </Box>
            </Box>
          </Box>

          <Box
            component="hr"
            sx={{
              border: 0,
              borderTop: `1px solid ${BRAND.color.divider}`,
              m: 0,
              mb: { xs: 3, md: 4 },
            }}
          />

          {esV2 ? (
            <DetallesV2 data={resultado} />
          ) : (
            resultado.detalles && <DetallesV1 data={resultado} />
          )}
        </Card>
      </PageContainer>
    );
  }

  // Fallback defensivo — no debería alcanzarse
  return (
    <PageContainer>
      <Card>
        <Encabezado
          eyebrow="Estado desconocido"
          titulo="No pudimos determinar el estado de este código"
        />
        <MensajeBody>
          Por favor recarga la página o intenta nuevamente.
        </MensajeBody>
      </Card>
    </PageContainer>
  );
};

PageContainer.propTypes = { children: PropTypes.node.isRequired };
Card.propTypes = { children: PropTypes.node.isRequired };
Encabezado.propTypes = {
  eyebrow: PropTypes.string.isRequired,
  titulo: PropTypes.string.isRequired,
};
MensajeBody.propTypes = { children: PropTypes.node.isRequired };

export default VerificarQR;
